import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Video as VideoIcon } from 'lucide-react';
import { JitsiCall } from '@/components/interview/JitsiCall';
import { useInterviewRecorder } from '@/hooks/useInterviewRecorder';
import { guestGet, guestState, type GuestView } from '@/services/interviewApi';
import { createProcessedCapture, type ProcessedCapture } from '@/utils/processedCapture';
import { joinCastingRoom, sendCastingRoomMessage, onCastingMessage, leaveCastingWs } from '@/services/castingWs';

// Digital zoom range for web guests (canvas center-crop) — matches casting's
// virtual-slate MAX_ZOOM and what the host panel's slider/stops expect.
const MAX_ZOOM = 4;
const ZOOM_STOPS = [1, 2, 3, 4];

// Mirror casting's open-in-app checks (utils.js): iOS WebKit or Android.
const isMobileSafari = () => { const ua = navigator.userAgent; return (!!ua.match(/iPad/i) || !!ua.match(/iPhone/i)) && !!ua.match(/WebKit/i); };
const isMobileAndroid = () => navigator.userAgent.toLowerCase().includes('android');
// iPadOS Safari reports a desktop Mac UA; touch points give it away.
const isIPadOS = () => /Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
const isPhoneOrTablet = () => isMobileSafari() || isMobileAndroid() || isIPadOS();

const InterviewGuestPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<GuestView | null>(null);
  const [gone, setGone] = useState(false);
  const [phase, setPhase] = useState<'welcome' | 'call'>('welcome');
  const [name, setName] = useState('');
  // Open-in-app prompt (casting pattern: org.hey.meet:// scheme + fragment payload)
  const [showAppPrompt, setShowAppPrompt] = useState(false);
  const [appUrl, setAppUrl] = useState('');
  const [mobile] = useState(isPhoneOrTablet);
  const [nameFromAccount, setNameFromAccount] = useState(false); // prefilled from a signed-in account
  const [camReady, setCamReady] = useState(false);
  const previewRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();
  const beatRef = useRef<ReturnType<typeof setInterval>>();
  const rec = useInterviewRecorder();
  const offsetRef = useRef(0);
  const handledTakeRef = useRef<string | null>(null);
  // Web-guest camera pipeline (casting parity): one camera → canvas (digital
  // zoom) → both the recorder and (feature-detected) the Jitsi call. The host
  // drives zoom over casting WS exactly like it does the phone app.
  const capRef = useRef<ProcessedCapture | null>(null);
  const castingIdsRef = useRef<{ sessionId: string; recordId: string } | null>(null);
  const [jitsiApi, setJitsiApi] = useState<any>(null);
  const [conferenceJoined, setConferenceJoined] = useState(false);
  const recRecordingRef = useRef(false);
  recRecordingRef.current = rec.recording;

  const reportCameraState = () => {
    const ids = castingIdsRef.current;
    const cap = capRef.current;
    if (!ids || !cap) return;
    sendCastingRoomMessage(`talent-session-${ids.sessionId}`, {
      type: 'camera-state', talentId: ids.recordId,
      facingMode: 'user', videoMuted: false,
      zoom: { currentUiZoom: cap.getZoom(), maxUiZoom: MAX_ZOOM, stops: ZOOM_STOPS, optical: false, warnAboveUiZoom: 2 },
      recordingQuality: '1080p', isRecording: recRecordingRef.current,
    });
  };

  // Poll interview state (slow pre-join, faster in call). Also tracks the
  // server-clock offset for the synchronized record start.
  useEffect(() => {
    if (!token) return;
    const tick = () => guestGet(token).then(v => { setView(v); offsetRef.current = v.serverNowMs - Date.now(); }).catch(e => { if (e.message === 'gone') setGone(true); });
    tick();
    pollRef.current = setInterval(tick, phase === 'call' ? 1500 : 5000);
    return () => clearInterval(pollRef.current);
  }, [token, phase]);

  // Drive recording off the server-stamped control (start on countdown, stop on idle).
  const rcState = view?.recordControl?.state;
  const rcTake = view?.recordControl?.takeId;
  useEffect(() => {
    if (phase !== 'call' || !token || !view) return;
    const c = view.recordControl;
    if (c.state === 'recording' && c.takeId && handledTakeRef.current !== c.takeId) {
      handledTakeRef.current = c.takeId;
      guestState(token, { recording: true }).catch(() => {});
      rec.start({ base: `/api/interview-guest/${token}/upload`, takeId: c.takeId, role: 'guest', includeVideo: true, startAtEpochMs: c.startAtEpochMs, clockOffset: offsetRef.current, stream: capRef.current?.stream }).catch(() => {});
    } else if (c.state === 'idle' && rec.recording) {
      rec.stop().then(() => guestState(token, { recording: false }).catch(() => {}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcState, rcTake, phase]);

  // Build the camera pipeline + casting WS control channel when entering the
  // call; tear both down on leave.
  useEffect(() => {
    if (phase !== 'call' || !token) return;
    let cancelled = false;
    navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: true }).then(src => {
      if (cancelled) { src.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = src;
      capRef.current = createProcessedCapture(src);
      // Shadow casting identity → join our command room and start reporting.
      fetch(`/api/interview-guest/${token}/casting`).then(r => (r.ok ? r.json() : null)).then(ids => {
        if (cancelled || !ids?.recordId) return;
        castingIdsRef.current = ids;
        joinCastingRoom(`talent-${ids.recordId}`);
        reportCameraState();
      }).catch(() => {});
    }).catch(() => { /* Jitsi still runs its own camera; zoom just won't be available */ });
    const off = onCastingMessage((m) => {
      if (m.type === 'set-camera-zoom' && capRef.current) {
        const z = Math.max(1, Math.min(MAX_ZOOM, Number(m.uiZoom) || 1));
        capRef.current.setZoom(z);
        reportCameraState();
      }
      if (m.type === 'get-camera-state') reportCameraState();
    });
    return () => {
      cancelled = true;
      off();
      leaveCastingWs();
      capRef.current?.stop(); capRef.current = null;
      streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null;
      setConferenceJoined(false); setJitsiApi(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, token]);

  // Hand Jitsi the processed (zoomable) track once the call is up. Clone — Jitsi
  // takes ownership of the track it's given and stops it on dispose; the
  // recorder keeps the original canvas track. Feature-detected: without
  // setLocalVideoTrack the call keeps its own camera and only the recording zooms.
  useEffect(() => {
    const cap = capRef.current;
    if (!jitsiApi || !conferenceJoined || !cap) return;
    if (typeof jitsiApi.setLocalVideoTrack !== 'function') return;
    cap.ready.then(() => jitsiApi.setLocalVideoTrack(cap.videoTrack.clone())).catch(() => {});
  }, [jitsiApi, conferenceJoined]);

  // Recording-state changes are part of camera-state (host disables the 4K
  // toggle mid-take on it).
  useEffect(() => { reportCameraState(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [rec.recording]);

  // Report upload progress to the host; when it reaches 100 report null so the
  // host clears the "Uploading…" chip (otherwise it freezes at 99% forever).
  useEffect(() => { if (token && rec.uploadPct != null) guestState(token, { uploadPct: rec.uploadPct >= 100 ? null : rec.uploadPct }).catch(() => {}); }, [rec.uploadPct, token]);
  useEffect(() => { if (token) guestState(token, { uploadFailed: rec.uploadFailed } as any).catch(() => {}); }, [rec.uploadFailed, token]);
  useEffect(() => {
    const uploading = rec.recording || (rec.uploadPct != null && rec.uploadPct < 100);
    if (!uploading) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [rec.recording, rec.uploadPct]);

  // If the guest is signed in, prefill their name from their account and skip
  // the name prompt entirely — only ask when we can't resolve it.
  useEffect(() => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!token) return;
    fetch('/api/auth/user', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const u = d?.user;
        // casting's verify-token returns snake_case (first_name); cover both.
        const n = (u && (u.name || [u.firstName || u.first_name, u.lastName || u.last_name].filter(Boolean).join(' ').trim())) || '';
        if (n) { setName(n); setNameFromAccount(true); }
      })
      .catch(() => {});
  }, []);

  // On phones, suggest the Hey Joe app (casting's talent-room UX). The backend
  // provisions a casting-shadow session and returns the ready-made
  // audition_data deep link the app already understands — zero app changes.
  // If the hand-off isn't configured (503) the prompt simply doesn't show.
  useEffect(() => {
    if (!view || !token || phase !== 'welcome') return;
    if (!isPhoneOrTablet()) return;
    let cancelled = false;
    fetch(`/api/interview-guest/${token}/app-link`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d?.deepLink) { setAppUrl(d.deepLink); setShowAppPrompt(true); } })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!view, phase]);

  // Camera preview on the welcome screen.
  useEffect(() => {
    if (phase !== 'welcome') return;
    let cancelled = false;
    navigator.mediaDevices.getUserMedia({ video: true, audio: true }).then(s => {
      if (cancelled) { s.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = s;
      if (previewRef.current) previewRef.current.srcObject = s;
      setCamReady(true);
      if (token) guestState(token, { camReady: true }).catch(() => {});
    }).catch(() => setCamReady(false));
    return () => { cancelled = true; };
  }, [phase, token]);

  const join = async () => {
    if (!token || !name.trim()) return;
    await guestState(token, { name: name.trim(), inCall: true, camReady: true, mobileBrowser: mobile }).catch(() => {});
    // Release the preview — the call-phase pipeline effect re-acquires the
    // camera and owns it from here (canvas zoom feeds recorder + call).
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setPhase('call');
    beatRef.current = setInterval(() => { guestState(token, { inCall: true, mobileBrowser: mobile }).catch(() => {}); }, 5000);
  };
  const openInApp = async () => {
    let url = appUrl;
    // Re-fetch with the resolved name — the mount-time fetch races
    // the account prefill, and the app greets by the payload name.
    try {
      const q = name.trim() ? `?name=${encodeURIComponent(name.trim())}` : '';
      const d = await fetch(`/api/interview-guest/${token}/app-link${q}`).then(r => (r.ok ? r.json() : null));
      if (d?.deepLink) url = d.deepLink;
    } catch { /* stale link still works */ }
    if (!url) return;
    try { navigator.clipboard.writeText(url); } catch { /* noop */ }
    window.open(url, '_self');
  };
  useEffect(() => () => { clearInterval(beatRef.current); streamRef.current?.getTracks().forEach(t => t.stop()); }, []);

  if (gone) return (
    <main className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
      <div className="text-center" style={{ maxWidth: 380 }}>
        <VideoIcon className="w-8 h-8 mx-auto" style={{ color: 'var(--hj-ink-4)' }} />
        <h1 className="font-hj-display m-0" style={{ marginTop: 14, fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>This interview link is no longer active.</h1>
        <p className="m-0" style={{ marginTop: 10, fontSize: 14, color: 'var(--hj-ink)' }}>Ask your host for a fresh link.</p>
      </div>
    </main>
  );

  if (!view) return <main className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-bg)' }}><Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} /></main>;

  if (phase === 'call') {
    return (
      // 100dvh: h-screen (100vh) can run under iOS Safari's bottom toolbar, hiding the call controls.
      <main className="h-screen flex flex-col overflow-hidden" style={{ background: '#000', height: '100dvh' }}>
        {(() => {
          const c = view.recordControl;
          if (rec.recording || c.state === 'recording') return <div className="flex items-center justify-center gap-2 flex-shrink-0" style={{ padding: 8, background: 'oklch(0.6 0.2 25)', color: '#fff', fontSize: 13, fontWeight: 700 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: '#fff' }} /> Recording</div>;
          if (rec.uploadFailed) return <div className="flex items-center justify-center gap-2 flex-shrink-0" style={{ padding: 8, background: 'oklch(0.6 0.2 25)', color: '#fff', fontSize: 13, fontWeight: 700 }}>That take didn&rsquo;t upload after several tries. Check your connection, then record it again.</div>;
          if (rec.uploadPct != null && rec.uploadPct < 100) return <div className="flex items-center justify-center gap-2 flex-shrink-0" style={{ padding: 8, background: 'var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700 }}>Uploading your video — {rec.uploadPct}% — please keep this tab open</div>;
          if (mobile) return <div className="flex items-center justify-center gap-3 flex-shrink-0 flex-wrap" style={{ padding: '8px 12px', background: 'oklch(0.85 0.15 85)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, textAlign: 'center' }}>You're recording in a browser, so your takes will be lower quality.{appUrl && <button type="button" onClick={openInApp} style={{ padding: '5px 12px', borderRadius: 999, background: 'oklch(0.18 0.005 70)', border: 0, color: '#fff', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>Open in the Hey Joe app</button>}</div>;
          return null;
        })()}
        <div className="flex-1 min-h-0"><JitsiCall domain={view.domain} roomName={view.roomName} displayName={name || 'Guest'} onApi={setJitsiApi} onJoined={() => setConferenceJoined(true)} /></div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
      <div className="w-full" style={{ maxWidth: 460 }}>
        <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{view.title}</div>
        <h1 className="font-hj-display m-0" style={{ marginTop: 6, fontSize: 'clamp(26px, 5vw, 36px)', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1 }}>You're meeting {view.creatorName}.</h1>

        <div className="w-full overflow-hidden" style={{ marginTop: 20, borderRadius: 'var(--hj-r-md)', background: '#000', aspectRatio: '16/9', border: '1px solid var(--hj-hairline)' }}>
          <video ref={previewRef} autoPlay muted playsInline className="w-full h-full" style={{ objectFit: 'cover', transform: 'scaleX(-1)' }} />
        </div>
        {!camReady && <p className="m-0" style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-ink-3)' }}>Allow camera & mic to get set up.</p>}

        {nameFromAccount ? (
          <p className="m-0" style={{ marginTop: 16, fontSize: 14, color: 'var(--hj-ink-2)' }}>Joining as <strong style={{ color: 'var(--hj-ink)' }}>{name}</strong>.</p>
        ) : (
          /* data-paper only retints shadcn controls; it does NOT remap the ink
             vars. This plain input still sits on the dark page, so its
             --hj-surface-2 / --hj-ink pair is the correct (dark) combination. */
          <div style={{ marginTop: 16 }} data-paper="beige">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={{ width: '100%', padding: '12px 14px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-2)', color: 'var(--hj-ink)', fontSize: 16, fontFamily: 'inherit' }} />
          </div>
        )}

        <p className="m-0" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--hj-ink-3)' }}>This call will be recorded.</p>

        {mobile && !showAppPrompt && (
          <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'oklch(0.85 0.15 85)', color: 'oklch(0.18 0.005 70)', fontSize: 13.5, fontWeight: 600, lineHeight: 1.45 }}>
            You're in a browser. Your takes will be lower quality than in the Hey Joe app.
            {appUrl && <button type="button" onClick={openInApp} style={{ display: 'block', marginTop: 8, padding: '7px 14px', borderRadius: 999, background: 'oklch(0.18 0.005 70)', border: 0, color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>Open in the Hey Joe app</button>}
          </div>
        )}

        <button type="button" onClick={join} disabled={!name.trim() || !camReady} className="w-full" style={{ marginTop: 16, padding: '14px', borderRadius: 999, background: 'var(--hj-blue)', border: 0, color: 'oklch(0.18 0.005 70)', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: (name.trim() && camReady) ? 'pointer' : 'not-allowed', opacity: (name.trim() && camReady) ? 1 : 0.5 }}>
          Join interview
        </button>
      </div>

      {/* Open-in-app prompt — casting's pattern, for the best mobile experience. */}
      {showAppPrompt && (
        <div className="fixed inset-0 flex items-end justify-center sm:items-center" style={{ zIndex: 80, background: 'oklch(0 0 0 / 0.55)', padding: 16 }}>
          <div className="w-full" style={{ maxWidth: 420, background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)', padding: 20 }}>
            <h2 className="font-hj-display m-0" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--hj-ink)' }}>Best on the Hey Joe app</h2>
            <p className="m-0" style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5, color: 'var(--hj-ink-2)' }}>This interview records your camera locally for full quality — the Hey Joe app handles that best on phones.</p>
            <div className="flex flex-col gap-2" style={{ marginTop: 16 }}>
              <button type="button" onClick={openInApp} className="w-full" style={{ padding: '13px', borderRadius: 999, background: 'var(--hj-blue)', border: 0, color: 'oklch(0.18 0.005 70)', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>Open in the app</button>
              <button type="button" onClick={() => setShowAppPrompt(false)} className="w-full" style={{ padding: '11px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>Continue in browser</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default InterviewGuestPage;
